// The refinement pass, which has no interface.
//
// The genre answers give every artist one of four numbers, so forty dubstep acts
// all seed at the same score and the solver has nothing to choose between them.
// This asks a model to separate them, in batches, in the background, and writes
// what comes back as ordinary seeded ratings. A rating the person set by hand is
// never touched, the solve simply gets better, and if the call fails nothing on
// screen changes or says so.
//
// It runs once per set of answers. The answers are hashed into a key, so
// changing them runs it again and reopening the site does not.

import { useEffect, useRef } from 'react';
import { useApp } from './store.tsx';
import { readProfileForSolver } from './profile-defaults.ts';
import type { Artist, Profile, Rating } from '../engine/types.ts';

const BATCH = 40;
const DONE_KEY = 'tasteRefinedFor';

/** A short stable string for one set of preference answers. */
function fingerprint(profile: Profile): string {
  const weights = Object.entries(profile.subgenreWeights ?? {})
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => `${k}:${v}`)
    .join(',');
  return `${profile.goalPriority}|${profile.staminaCurve}|${profile.energyPref}|${weights}`;
}

/** The preference answers as a sentence the model can read. */
function describe(profile: Profile): string {
  const weights = profile.subgenreWeights ?? {};
  const loves = Object.keys(weights).filter((k) => weights[k] === 'love');
  const likes = Object.keys(weights).filter((k) => weights[k] === 'like');
  const nos = Object.keys(weights).filter((k) => weights[k] === 'no');

  const goal = {
    max_music: 'wants as much music as possible',
    stay_together: 'would rather stay with their crew than optimize',
    headliners_and_rest: 'wants the headliners and is happy to rest around them',
    discover: 'wants to find acts they have never heard',
  }[profile.goalPriority];

  return [
    loves.length > 0 ? `Loves ${loves.join(', ')}.` : '',
    likes.length > 0 ? `Likes ${likes.join(', ')}.` : '',
    nos.length > 0 ? `Not interested in ${nos.join(', ')}.` : '',
    goal ? `They ${goal}.` : '',
    profile.energyPref >= 0.7
      ? 'Prefers the heaviest, highest-energy sets.'
      : profile.energyPref <= 0.3
        ? 'Prefers the slower and more melodic end.'
        : '',
  ]
    .filter(Boolean)
    .join(' ');
}

async function scoreBatch(
  note: string,
  artists: Artist[],
): Promise<{ id: string; score: number }[]> {
  try {
    const response = await fetch('/api/taste', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        note,
        artists: artists.map((a) => ({
          id: a.id,
          name: a.name,
          genres: (a.genreTags ?? []).join(', '),
        })),
      }),
    });
    if (!response.ok) return [];
    const data = (await response.json()) as { scores?: { id: string; score: number }[] };
    return data.scores ?? [];
  } catch {
    return [];
  }
}

export function TasteRefiner() {
  const { state, actions, storage: store } = useApp();
  const running = useRef(false);

  useEffect(() => {
    if (!state.ready || running.current) return;

    const stored = state.profiles.find((p) => p.userId === state.userId);
    if (!stored || state.artists.length === 0) return;
    // Read with defaults, so a blank answer hashes and reads as it always has.
    const profile = readProfileForSolver(stored, state.userId);

    running.current = true;

    void (async () => {
      const key = fingerprint(profile);
      const already = await store.meta<string>(DONE_KEY);
      if (already === key) return;

      const note = describe(profile);
      if (!note) return;

      const manual = new Set(
        state.ratings
          .filter((r) => r.userId === state.userId && r.source === 'manual')
          .map((r) => r.artistId),
      );
      const pending = state.artists.filter((a) => !manual.has(a.id));
      const byId = new Map(pending.map((a) => [a.id, a]));
      let written = 0;

      for (let i = 0; i < pending.length; i += BATCH) {
        const scores = await scoreBatch(note, pending.slice(i, i + BATCH));
        if (scores.length === 0) continue;

        const rows: Rating[] = [];
        for (const s of scores) {
          if (!byId.has(s.id)) continue;
          rows.push({
            userId: state.userId,
            artistId: s.id,
            score: Math.min(10, Math.max(1, Math.round(s.score))),
            source: 'seeded',
            // Above a tag guess and below anything the person said themselves.
            confidence: 0.55,
            updatedAt: new Date().toISOString(),
          });
        }
        await store.putAll('ratings', rows);
        written += rows.length;
      }

      await store.setMeta(DONE_KEY, key);
      // One reload at the end rather than one per batch, so the grid restyles
      // once with better numbers instead of flickering through them.
      if (written > 0) await actions.reload();
    })();
  }, [state.ready, state.profiles, state.artists, state.ratings, state.userId, actions]);

  return null;
}
